import { FC, useMemo } from "react";
import { format } from "date-fns";
import { uk } from "date-fns/locale/uk";
import { Divider, Image, Typography } from "theme";
import { Calendar } from "theme/icons";

import { Box, Chip, Grid2, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";

import { BookingResponse } from "../../../api/hooks/useBookingById/type.ts";
import { EquipmentKits, KitConfig } from "../../../api/hooks/useEquipmentKits/type.ts";
import EditPrice from "../../Forms/SummaryForm/components/EditPrice.tsx";
import Loyalty from "../../Forms/SummaryForm/components/Loyalty.tsx";

import CancelBooking from "./components/CancelBooking.tsx";
import Payment from "./components/Payment.tsx";
import PriceHistoryDisplay from "./components/PriceHistoryDisplay.tsx";

type BookingInfoProps = {
  booking: BookingResponse;
};

const PaymentStatus: FC<{
  status: string;
}> = ({ status }) => {
  switch (status) {
    case "PaymentPending":
      return <Chip label="Очікує оплати" color="warning" />;
    case "PaymentCompleted":
      return (
        <Chip
          label="Оплачено"
          color="success"
          style={{
            color: "white",
          }}
        />
      );
    case "PaymentMade":
      return (
        <Chip
          label="Підверджено"
          style={{
            color: "white",
            backgroundColor: "orange",
          }}
        />
      );
    case "PaymentCanceled":
      return <Chip label="Скасовано" color="error" />;
    default:
      return null;
  }
};

const StyledImage = styled("img")(({ theme }) => {
  return {
    height: "88px",
    width: "88px",
    objectFit: "contain",
    backgroundColor: theme.palette.common.white,
  };
});

const BookingInfo: FC<BookingInfoProps> = ({ booking }) => {
  const theme = useTheme();

  const selectedTime = useMemo(() => {
    return `${format(booking.hallDetails.startDate, "EEEE, dd MMMM HH:mm", {
      locale: uk,
    })} - ${format(booking.hallDetails.endDate, "HH:mm")}`;
  }, [booking.hallDetails.endDate, booking.hallDetails.startDate]);

  return (
    <Box>
      <Divider />

      <Box my={2.5}>
        <PaymentStatus status={booking.paymentStatus} />
      </Box>

      <Box my={2.5}>
        <Typography variant="h2" textTransform="uppercase">
          Інформація про бронювання:
        </Typography>
        <Grid2 container columnGap={0.5} alignItems="center" mt={1.25}>
          <Calendar />
          <Typography variant="body1">{selectedTime}</Typography>
        </Grid2>
        <Grid2 container columnGap={0.5} alignItems="center" mt={1.25}>
          Ім'я:
          <Typography variant="body1" weight="semiBold">
            {booking.clientDetails.name}
          </Typography>
        </Grid2>
        <Grid2 container columnGap={0.5} alignItems="center" mt={1.25}>
          Телефон:
          <a href={`tel:${booking.clientDetails.phone}`}>
            <Typography variant="body1" weight="semiBold">
              {booking.clientDetails.phone}
            </Typography>
          </a>
        </Grid2>
        <Grid2 container columnGap={0.5} alignItems="center" mt={1.25}>
          Набір обладнання:
          <Typography variant="body1" weight="semiBold">
            {KitConfig[booking.defaultEquipment as EquipmentKits] ?? "-"}
          </Typography>
        </Grid2>
        {booking.clientDetails.email && (
          <Grid2 container columnGap={0.5} alignItems="center" mt={1.25}>
            Email:
            <a href={`mailto:${booking.clientDetails.email}`}>
              <Typography variant="body1" weight="semiBold">
                {booking.clientDetails.email}
              </Typography>
            </a>
          </Grid2>
        )}

        {booking.paymentLink && (
          <Grid2 container columnGap={0.5} alignItems="center" mt={1.25}>
            Посилання на оплату:
            <a href={booking.paymentLink}>
              <Typography variant="body1" weight="semiBold">
                {booking.paymentLink}
              </Typography>
            </a>
          </Grid2>
        )}

        {booking.clientDetails.comment && (
          <Grid2 container columnGap={0.5} alignItems="center" mt={1.25}>
            Коментар до замовлення:
            <Typography variant="body1" weight="semiBold">
              {booking.clientDetails.comment}
            </Typography>
          </Grid2>
        )}
      </Box>

      <Divider />

      <Box my={2.5}>
        <Typography variant="h2" textTransform="uppercase">
          Інформація про Оренду
        </Typography>

        <Grid2 mt={2.5} width="100%" container justifyContent="space-between" alignItems="center">
          <Box>
            <Grid2 container columnGap={1.5}>
              <Image src={booking.hallDetails.imageUrl} />
              <Box>
                <Typography
                  textTransform="uppercase"
                  weight="semiBold"
                  variant="body1"
                  color="primary"
                  mb={0.5}
                >
                  {booking.hallDetails.title}
                </Typography>
                <Typography
                  textTransform="uppercase"
                  weight="light"
                  variant="body1"
                  color="primary"
                  mb={1.25}
                >
                  {booking.hallDetails.defaultPricePerHour}грн
                </Typography>
                <Box>
                  <Grid2 container alignItems="center" columnGap={0.75}>
                    <Typography
                      weight="regular"
                      variant="body1"
                      color="primary"
                      textTransform="capitalize"
                    >
                      {`${format(booking.hallDetails.startDate, "HH:mm")} - ${format(booking.hallDetails.endDate, "HH:mm")}`}
                    </Typography>
                  </Grid2>
                </Box>
              </Box>
            </Grid2>
          </Box>
        </Grid2>
      </Box>

      <Divider />

      {booking.equipmentsDetails.length > 0 && (
        <>
          <Box my={2.5}>
            {booking.equipmentsDetails.map((eq) => {
              return (
                <Grid2
                  key={eq.id}
                  columnGap={1.5}
                  container
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <Grid2 my={1.25} container columnGap={1.5} flexWrap="nowrap">
                      <Box>
                        <StyledImage src={eq.imageUrl} />
                      </Box>
                      <Box>
                        <Typography variant="body1" weight="regular" textTransform="uppercase">
                          {eq.title}
                        </Typography>

                        <Typography my={0.5} variant="body2" weight="regular">
                          Кількість: {eq.bookedCount}
                        </Typography>

                        <Typography my={0.5} variant="body2" weight="regular">
                          Час оренди:{" "}
                          {`${format(eq.startDate, "HH:mm")} - ${format(eq.endDate, "HH:mm")}`}
                        </Typography>

                        <Typography variant="body2" weight="semiBold" textTransform="uppercase">
                          {eq.pricePerUnit} {eq.isFixedPrice ? "грн" : "грн/год"}
                        </Typography>
                      </Box>
                    </Grid2>
                  </Box>
                </Grid2>
              );
            })}
          </Box>
          <Divider />
        </>
      )}

      <PriceHistoryDisplay />

      <Box my={2.5} maxWidth="300px">
        <Grid2 mb={1.5} container justifyContent="space-between" alignItems="center">
          <Typography variant="body1" textTransform="uppercase" weight="semiBold">
            Сума послуг:
          </Typography>
          <Typography variant="body1" textTransform="uppercase" weight="semiBold">
            {booking?.totalPrice} грн
          </Typography>
        </Grid2>
        {!!booking?.discountFromPromocode && (
          <Grid2 container justifyContent="space-between" alignItems="center">
            <Typography
              style={{
                color: theme.palette.success.main,
              }}
              variant="body1"
              textTransform="uppercase"
              weight="semiBold"
            >
              Сума знижки:
            </Typography>
            <Typography
              style={{
                color: theme.palette.success.main,
              }}
              variant="body1"
              textTransform="uppercase"
              weight="semiBold"
            >
              {Number(booking?.discountFromPromocode)} грн
            </Typography>
          </Grid2>
        )}
        <Grid2 mb={1.5} container justifyContent="space-between" alignItems="center">
          <Typography variant="body1" textTransform="uppercase" weight="semiBold">
            Сплачено:
          </Typography>
          <Typography variant="body1" textTransform="uppercase" weight="semiBold">
            {booking?.alreadyPaid} грн
          </Typography>
        </Grid2>

        {booking.paymentStatus !== ("PaymentCanceled" as any) && (
          <>
            <Box my={2}>
              <Divider />
            </Box>

            <Grid2 container justifyContent="space-between" alignItems="center">
              <Typography variant="body1" textTransform="uppercase" weight="bold">
                До оплати:
              </Typography>
              <Grid2 container alignItems="center" gap={1}>
                <Loyalty customerId={String(booking.clientDetails.id)}/>
                <EditPrice
                  key={booking?.totalPriceWithDiscounts}
                  initialPrice={booking?.totalPriceWithDiscounts}
                />
                <Typography variant="body1" textTransform="uppercase" weight="bold">
                  {booking?.totalPriceWithDiscounts - booking?.alreadyPaid} грн
                </Typography>
              </Grid2>
            </Grid2>

            <Box my={2}>
              <Payment amount={booking?.totalPriceWithDiscounts - booking?.alreadyPaid} />
            </Box>

            <Box>
              <CancelBooking />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default BookingInfo;
